import React, { useState } from 'react';
import { Smartphone, ChevronRight, Info, AlertCircle, CheckCircle } from 'lucide-react';

const HaikuSetup = () => {
  const [expandedSection, setExpandedSection] = useState('overview');

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="space-y-4">
      {/* Overview Section */}
      <div className="bg-white rounded-xl border-2 border-blue-500 overflow-hidden">
        <button
          onClick={() => toggleSection('overview')}
          className="w-full p-4 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 text-white rounded-lg">
              <Info size={20} />
            </div>
            <div className="text-left">
              <div className="font-bold text-blue-900">What is Haiku/Canto?</div>
              <div className="text-sm text-blue-700 mt-0.5">Overview and requirements</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-blue-600 transition-transform ${
              expandedSection === 'overview' ? 'rotate-90' : ''
            }`}
          />
        </button>

        {expandedSection === 'overview' && (
          <div className="p-4 border-t-2 border-gray-200 bg-gray-50 space-y-3">
            <p className="text-gray-700 leading-relaxed">
              <strong>Epic Haiku</strong> and <strong>Epic Canto</strong> are mobile applications that allow you to access Epic on your smartphone or tablet.
            </p>
            <ul className="space-y-2 text-sm text-gray-700 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Haiku</strong> - For iPhone/iPad (iOS)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-0.5">•</span>
                <span><strong>Canto</strong> - For Android devices</span>
              </li>
            </ul>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded mt-4">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  <strong>Important:</strong> You must configure the app before you can log in. Simply downloading the app is not enough!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 1: Download */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection('download')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Smartphone size={20} className="text-green-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900">Step 1: Download the App</div>
              <div className="text-sm text-gray-600 mt-0.5">From Apple App Store or Google Play Store</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              expandedSection === 'download' ? 'rotate-90' : ''
            }`}
          />
        </button>

        {expandedSection === 'download' && (
          <div className="p-4 border-t-2 border-gray-200 bg-gray-50 space-y-4">
            <div className="space-y-3">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">i</span>
                  iPhone/iPad Users
                </h4>
                <p className="text-sm text-gray-700">
                  Search for <strong>"Haiku"</strong> in the Apple App Store and download <strong>Epic Haiku</strong>
                </p>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">A</span>
                  Android Users
                </h4>
                <p className="text-sm text-gray-700">
                  Search for <strong>"Canto"</strong> in the Google Play Store and download <strong>Epic Canto</strong>
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  Do not attempt to log in yet! Continue to Step 2 to configure the app first.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 2: Configure */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection('configure')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CheckCircle size={20} className="text-purple-600" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-gray-900">Step 2: Configure the App</div>
              <div className="text-sm text-gray-600 mt-0.5">Set up your connection to CommonSpirit</div>
            </div>
          </div>
          <ChevronRight
            size={20}
            className={`text-gray-400 transition-transform ${
              expandedSection === 'configure' ? 'rotate-90' : ''
            }`}
          />
        </button>

        {expandedSection === 'configure' && (
          <div className="p-4 border-t-2 border-gray-200 bg-gray-50 space-y-4">
            <div className="space-y-4">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                <div className="flex items-start gap-2">
                  <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-semibold mb-1">Configuration is required before first login</p>
                    <p>You'll need to visit a special URL to connect your device to CommonSpirit's Epic system.</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Configuration Steps:</h4>
                    <ol className="space-y-3 text-sm text-gray-700">
                      <li className="flex gap-3">
                        <span className="font-bold text-blue-600 flex-shrink-0">1.</span>
                        <span>Open a web browser on your mobile device (Safari, Chrome, etc.)</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-bold text-blue-600 flex-shrink-0">2.</span>
                        <div className="flex-1">
                          <span>Navigate to:</span>
                          <div className="bg-gray-100 p-2 rounded mt-1 font-mono text-xs break-all">
                            https://haiku.catholichealth.net/
                          </div>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-bold text-blue-600 flex-shrink-0">3.</span>
                        <span>You'll be prompted to select your <strong>Market</strong></span>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-bold text-blue-600 flex-shrink-0">4.</span>
                        <div className="flex-1">
                          <span>When prompted to select your Market, choose:</span>
                          <div className="mt-2 bg-blue-50 p-3 rounded-lg border-2 border-blue-200">
                            <span className="font-mono font-bold text-blue-600 text-lg">SOU</span>
                            <span className="text-gray-700 ml-2">(South Market)</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-2 italic">
                            Texas Heart Institute and BSLMC are part of the South Market
                          </p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-bold text-blue-600 flex-shrink-0">5.</span>
                        <span>Your device will automatically configure the app settings</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="font-bold text-blue-600 flex-shrink-0">6.</span>
                        <span>Once configuration is complete, you can open the Haiku/Canto app and log in using your <strong>BSLMC Epic username and password</strong></span>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
                <div className="flex items-start gap-2">
                  <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-900">
                    <p className="font-semibold mb-1">Configuration Complete!</p>
                    <p>After completing these steps, you can log in to Haiku/Canto using your Epic credentials.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Troubleshooting */}
      <div className="bg-amber-50 rounded-lg p-4 border-l-4 border-amber-600">
        <h3 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
          <AlertCircle size={18} />
          Troubleshooting
        </h3>
        <ul className="space-y-2 text-sm text-amber-900">
          <li className="flex items-start gap-2">
            <span className="font-bold flex-shrink-0">•</span>
            <span>
              <strong>Can't log in?</strong> Make sure you completed the configuration step via the haiku.catholichealth.net URL first
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold flex-shrink-0">•</span>
            <span>
              <strong>Wrong market selected?</strong> Delete and reinstall the app, then reconfigure with the correct market
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold flex-shrink-0">•</span>
            <span>
              <strong>Still having issues?</strong> Contact BSLMC IT Support at 832-355-8000
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default HaikuSetup;
